/**
 * The `File Import` tile data source.
 *
 * A `CustomTile` generated from an imported spreadsheet carries a query with
 * `dataSource === 'File Import'`, an `importedDatasetId`, and a `widgetId`. When the tile renders on a
 * dashboard (i.e. outside the import preview, where data is fed inline), the tile fetch pipeline calls
 * `resolveFileImportData` to pull the matching widget's pre-aggregated rows out of the persisted
 * `ImportedDataset`. This mirrors the existing `'Local File'` branch, but reads a real DB row rather
 * than a build-time bundled JSON file, so the tiles work in production.
 */
import { fetchItem } from '../tileQueries';

export const FILE_IMPORT_SOURCE = 'File Import';

export interface FileImportQueryLike {
  dataSource?: string;
  importedDatasetId?: number;
  widgetId?: string;
}

/** True when a query should be served from a persisted `ImportedDataset` rather than fetched by path. */
export function isFileImportQuery(q: FileImportQueryLike | undefined | null): boolean {
  return !!q && q.dataSource === FILE_IMPORT_SOURCE && q.importedDatasetId != null;
}

/**
 * Resolve the rows for a `File Import` query: the named widget's aggregated `data`, or the dataset's
 * raw `rows` when no `widgetId` is given. Returns `[]` on any miss so a tile degrades to empty rather
 * than throwing inside the render pipeline.
 */
export async function resolveFileImportData(q: FileImportQueryLike): Promise<any[]> {
  if (!q?.importedDatasetId) return [];
  try {
    const dataset: any = await fetchItem(`db/imported-dataset/${q.importedDatasetId}`);
    if (!dataset) return [];
    if (q.widgetId) {
      const widget = (dataset.widgets || []).find((w: any) => w?.id === q.widgetId);
      return Array.isArray(widget?.data) ? widget.data : [];
    }
    return Array.isArray(dataset.rows) ? dataset.rows : [];
  } catch {
    return [];
  }
}
